import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";

const CircularGaugeChart = ({ value }) => {
  // Ensure value is a valid number, default to 0 if invalid
  const [displayValue, setDisplayValue] = useState(0);

  // Update displayValue when value prop changes
  useEffect(() => {
    const newValue =
      value === Infinity
        ? 0
        : typeof value === "number" && !isNaN(value)
        ? Math.max(0, Math.min(100, value))
        : 0;
    setDisplayValue(newValue);
  }, [value]);

  // Function to determine color based on value
  const getColorForValue = (val) => {
    if (val <= 25) return "rgb(214, 93, 99)"; // Red
    if (val <= 50) return "rgb(201, 176, 75)"; // Yellow
    if (val <= 75) return "rgb(66, 166, 190)"; // Cyan
    return "rgb(92, 190, 133)"; // Green
  };

  const getOption = () => {
    const pointerColor = getColorForValue(displayValue);

    const labelSeries = [
      { grade: "Grade D", angle: 202.5 },
      { grade: "Grade C", angle: 157.5 },
      { grade: "Grade B", angle: 112.5 },
      { grade: "Grade A", angle: 67.5 },
    ].map(({ grade, angle }) => {
      const rad = (angle * Math.PI) / 180;
      const x = Math.cos(rad) * 110;
      const y = Math.sin(rad) * 110;
      return {
        type: "gauge",
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: 100,
        pointer: { show: false },
        progress: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        detail: { show: false },
        title: {
          show: true,
          offsetCenter: [`${x}%`, `${y}%`],
          fontSize: 14,
          color: "black",
          text: grade,
        },
        data: [{ value: 0 }],
        tooltip: { show: false },
      };
    });

    return {
      tooltip: {
        show: true,
        formatter: (params) => {
          if (params.seriesIndex === 0) {
            return `Target achieved: ${params.value}%`;
          }
          return "";
        },
      },
      series: [
        {
          type: "gauge",
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 100,
          radius: "90%",
          center: ["50%", "50%"],
          splitNumber: 10,
          progress: {
            show: true,
            width: 6,
            itemStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 1,
                x2: 1,
                y2: 0,
                colorStops: [
                  { offset: 0, color: "rgb(167, 70, 75)" },
                  { offset: 0.25, color: "rgb(148, 129, 55)" },
                  { offset: 0.5, color: "rgb(49, 113, 129)" },
                  { offset: 0.75, color: "rgb(44, 114, 73)" },
                ],
              },
            },
          },
          axisLine: {
            lineStyle: {
              width: 13,
              color: [
                [0.25, "#FF6E76"],
                [0.5, "#FDDD60"],
                [0.75, "#58D9F9"],
                [1, "#7CFFB2"],
              ],
            },
          },
          pointer: {
            icon: "rect",
            width: 6,
            length: "50%",
            itemStyle: {
              color: pointerColor,
            },
          },
          anchor: {
            show: true,
          },
          axisTick: {
            distance: 0,
            length: 6,
            lineStyle: { color: "#888" },
          },
          splitLine: {
            distance: 0,
            length: 10,
            lineStyle: { color: "gray", width: 2 },
          },
          axisLabel: {
            show: false,
          },
          detail: {
            valueAnimation: true,
            formatter: (val) => `${val}%`, // Explicitly format to avoid NaN
            color: pointerColor,
            fontSize: 15,
            offsetCenter: [0, "20%"],
          },
          title: {
            show: false,
          },
          data: [{ value: displayValue }],
        },
        ...labelSeries,
      ],
    };
  };

  // Show loading state if value is not yet valid
  if (typeof value !== "number" || isNaN(value)) {
    return <div>Loading...</div>;
  }

  return (
    <div
      style={{
        height: 150,
        width: 150,
      }}
    >
      <ReactECharts
        option={getOption()}
        style={{
          height: "100%",
          width: "100%",
        }}
        notMerge={true}
        key={displayValue} // Force re-render on value change
      />
    </div>
  );
};

export default CircularGaugeChart;
